// Consent-gated Google Analytics 4 loader.
//
// This is a public-health, public-good map. Analytics are STRICTLY opt-in:
// gtag is never loaded, and no GA cookie is ever set, until the visitor
// explicitly accepts via the cookie-consent banner. On accept we load gtag
// with `anonymize_ip: true` and minimal config. On reject we load nothing.
//
// The visitor's choice persists in localStorage under CONSENT_KEY:
//   'granted' → load gtag silently on every subsequent visit
//   'denied'  → never load
//   (unset)   → show the banner, load nothing yet

export const CONSENT_KEY = 'cookie-consent';
export type ConsentValue = 'granted' | 'denied';

// GA4 Measurement ID for ebolamap.app (provisioned in the shared Atlas GA property).
const GA_MEASUREMENT_ID = 'G-G4ZREM0MJF';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __gaLoaded?: boolean;
  }
}

/** Read the stored consent choice, or null if the visitor hasn't chosen. */
export function getConsent(): ConsentValue | null {
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

/** Persist the visitor's choice. */
export function setConsent(value: ConsentValue): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* private browsing — choice just won't persist */
  }
}

/**
 * Inject the GA4 gtag.js script and initialise it with IP anonymisation.
 * Idempotent: safe to call more than once. Never call this before the
 * visitor has granted consent.
 */
export function loadAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (window.__gaLoaded) return;
  window.__gaLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    // No ad personalisation signals from a public-health tracker.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

/**
 * Apply the stored consent choice on app boot. If the visitor previously
 * granted consent, load gtag silently. Returns the stored value (or null).
 */
export function initAnalyticsFromStoredConsent(): ConsentValue | null {
  const choice = getConsent();
  if (choice === 'granted') loadAnalytics();
  return choice;
}
